import React from 'react';
import { createPortal } from 'react-dom';
import type { TicketStatusCategory } from '../types';
import { TICKET_STATUS_CATEGORIES } from '../types';
import { Icon } from '@/shared/icons'
interface StageChangeConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  fromStageName: string;
  toStageName: string;
  fromCategory: TicketStatusCategory;
  toCategory: TicketStatusCategory;
  isLoading?: boolean;
  /** Sobrescreve o título padrão "Mudança de estado". */
  title?: string;
  /** Sobrescreve a mensagem de aviso padrão (ex: dependência de projeto). */
  message?: string;
}

function getTransitionMessage(from: TicketStatusCategory, to: TicketStatusCategory): string {
  const fromBehavior = TICKET_STATUS_CATEGORIES[from].slaBehavior;
  const toBehavior = TICKET_STATUS_CATEGORIES[to].slaBehavior;
  const isFromTerminal = from === 'resolved' || from === 'closed';
  const isToTerminal = to === 'resolved' || to === 'closed';

  if (isFromTerminal && !isToTerminal) {
    if (toBehavior === 'paused') return 'Isso vai reabrir o ticket em estado de espera.';
    return 'Isso vai reabrir o ticket e reativar o SLA.';
  }
  if (isToTerminal) {
    if (to === 'resolved') return 'Isso vai resolver o ticket e parar o SLA.';
    return 'Isso vai fechar o ticket e parar o SLA.';
  }
  if (fromBehavior === 'running' && toBehavior === 'paused') {
    return 'Isso vai pausar o ticket e o SLA.';
  }
  if (fromBehavior === 'paused' && toBehavior === 'running') {
    return 'Isso vai retomar o ticket e reativar o SLA.';
  }
  return 'Isso vai alterar o estado do ticket.';
}

export const StageChangeConfirmation: React.FC<StageChangeConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  fromStageName,
  toStageName,
  fromCategory,
  toCategory,
  isLoading = false,
  title,
  message: messageOverride,
}) => {
  if (!isOpen) return null;

  const message = messageOverride ?? getTransitionMessage(fromCategory, toCategory);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-[60] p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div
        className="rounded-lg shadow-xl max-w-md w-full"
        style={{ backgroundColor: '#ffffff' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Icon name="alert-triangle" className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
              {title ?? 'Mudança de estado'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            disabled={isLoading}
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="mb-3" style={{ color: '#1e293b' }}>
            Você está movendo de <strong>&quot;{fromStageName}&quot;</strong> para <strong>&quot;{toStageName}&quot;</strong>.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">{message}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t" style={{ borderColor: '#e5e7eb' }}>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border disabled:opacity-50 transition-colors"
            style={{ color: '#1e293b', backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-colors"
            style={{ backgroundColor: '#8b5cf6' }}
            onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#7c3aed'; }}
            onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
            disabled={isLoading}
          >
            {isLoading ? 'Confirmando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
